import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoCheckmarkCircle, IoRefresh, IoArrowBack } from "react-icons/io5";
import ProcessingMascot from "../../assets/logo.png";

// Components
import Button from "../../button";
import Checkmark from "./checkmark.png"
import Target from "./target.png"
import Clock from "./clock.png"

// Styles
import styles from "./Finished.module.css";

// Types
import { CardSet, Card } from "../../../types";

interface FinishedProps {
  cardSet: CardSet;
  cardProgress: Record<string, boolean | undefined>;
  duration: number;
  initialLearnedCardIds: Set<string>;
  onBack: () => void;
  onComplete: () => void;
}

const splitCardProgressIntoCorrectIncorrectAndSkippedCounts = (
  cardProgress: Record<string, boolean | undefined>,
  cards: Card[],
  initialLearnedCardIds: Set<string>
): {
  correct: number,
  incorrect: number,
  skipped: number,
  total: number,
  newCorrectCount: number,
  learnedCorrectCount: number
} => {
  let correct = 0;
  let incorrect = 0;
  let skipped = 0;
  let newCorrectCount = 0;
  let learnedCorrectCount = 0;

  cards.forEach(card => {
    const userAnswer = cardProgress[card.id];
    const wasInitiallyLearned = initialLearnedCardIds.has(card.id);

    if (userAnswer === true) {
      correct++;
      if (wasInitiallyLearned) {
        learnedCorrectCount++;
      } else {
        newCorrectCount++;
      }
    } else if (userAnswer === false) {
      incorrect++;
    } else {
      skipped++;
    }
  });

  return {
    correct,
    incorrect,
    skipped,
    total: cards.length,
    newCorrectCount,
    learnedCorrectCount
  }
}

const reportCardProgress = (
  cardSetId: string,
  duration: number,
  correct: number,
  incorrect: number,
  skipped: number,
  newCorrectCount: number,
  learnedCorrectCount: number
) => {
  fetch(`/card_sets/${cardSetId}/slides/record_attempt`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
    },
    body: JSON.stringify({
      completion_record: {
        duration,
        correct_count: correct,
        incorrect_count: incorrect,
        skip_count: skipped,
        new_correct_count: newCorrectCount,
        learned_correct_count: learnedCorrectCount
      }
    })
  })
}

const Finished: React.FC<FinishedProps> = ({
  cardSet,
  cardProgress,
  duration,
  initialLearnedCardIds,
  onBack,
  onComplete,
}) => {
  const { t } = useTranslation();

  const { correct, incorrect, skipped, total, newCorrectCount, learnedCorrectCount } = useMemo(() => {
    const result = splitCardProgressIntoCorrectIncorrectAndSkippedCounts(cardProgress, cardSet.cards, initialLearnedCardIds);
    return result;
  }, [cardProgress, cardSet.cards, initialLearnedCardIds]);

  const correctPercentage = useMemo(() => {
    return Math.round((correct / (correct + incorrect + skipped)) * 100);
  }, [correct, incorrect, skipped]);

  const timeInMinutesWithColon = useMemo(() => {
    const minutes = Math.floor(duration / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [duration]);

  useEffect(() => {
    reportCardProgress(cardSet.id, duration, correct, incorrect, skipped, newCorrectCount, learnedCorrectCount);
  }, [cardSet.id, duration, correct, incorrect, skipped, newCorrectCount, learnedCorrectCount]);

  const stats = useMemo(() => {
    const totalCards = cardSet.cards.length;
    const learnedCards = Object.values(cardSet.cards).filter(card => card.status === 'learned').length;
    const inProgressCards = Object.values(cardSet.cards).filter(card => card.status === 'in_progress').length;
    const notLearnedCards = Object.values(cardSet.cards).filter(card => card.status === 'created').length;
    const reviewedCards = learnedCards + inProgressCards;

    const completionPercentage = Math.round((reviewedCards / totalCards) * 100);
    const learningPercentage = Math.round((learnedCards / totalCards) * 100);

    return {
      totalCards,
      learnedCards,
      inProgressCards,
      notLearnedCards,
      completionPercentage,
      learningPercentage
    };
  }, [cardSet.cards.length, cardProgress]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <img src={ProcessingMascot} alt="Processing Mascot" className={styles.mascot} />
        <h1 className={`${styles.title} dark:text-white`}>{t('flashcardReview.niceJob')}</h1>
        <p className={`${styles.subtitle} dark:text-white`}>
          {t('flashcardReview.reviewComplete')}
        </p>
      </div>

      <div className={styles.stats}>
        <div className={`${styles.statCard} dark:bg-stone-650 dark:text-white`}>
          <div className="flex flex-row gap-2 items-center">
            <img src={Target} alt="Bullseye" className={styles.statIcon} />
            <span className={styles.statNumber}>{stats.learningPercentage}%</span>
          </div>
          <span className={`${styles.statLabel} dark:text-white`}>{t('flashcardReview.accuracy')}</span>
        </div>

        <div className={`${styles.statCard} dark:bg-stone-650 dark:text-white`}>
          <div className="flex flex-row gap-2 items-center">
            <img src={Checkmark} alt="Checkmark" className={styles.statIcon} />
            <span className={styles.statNumber}>{`${stats.learnedCards}/${total}`}</span>
          </div>
          <span className={`${styles.statLabel} dark:text-white`}>{t('flashcardReview.correct')}</span>
        </div>

        <div className={`${styles.statCard} dark:bg-stone-650 dark:text-white`}>
          <div className="flex flex-row gap-2 items-center">
            <img src={Clock} alt="Clock" className={styles.statIcon} />
            <span className={styles.statNumber}>{timeInMinutesWithColon}</span>
          </div>
          <span className={`${styles.statLabel} dark:text-white`}>{t('flashcardReview.time')}</span>
        </div>
      </div>

      <div className={styles.actions}>
        <Button
          type="primary"
          onClick={onBack}
          className={`${styles.actionButton} dark:bg-stone-650 dark:hover:bg-zeus-900`}
        >
          {t('actions.complete')}
        </Button>

        <Button
          type="secondary"
          onClick={onComplete}
          className={`${styles.actionButton} dark:bg-stone-650 dark:text-white dark:border-gray-400 dark:hover:bg-zeus-900`}
        >
          {t('flashcardReview.reviewAgain')}
        </Button>
      </div>
    </div>
  );
};

export default Finished;